<!--
    IMPORTANT: Keep this component reusable and generic.
    Do not add parent-specific business logic or styles here.
    Customize via props, scoped slots, and emitted events from parents instead.
    Preserve the prop types and value structures exactly as shown in the examples below.
-->
<template>
    <div class="base-table-container">
        <div class="table-wrapper" :class="{ 'has-pagination': hasPagination }">
            <table class="base-table">
                <thead v-if="headers && headers.length > 0" class="headers">
                    <tr>
                        <th v-for="header in headers" :key="header.key" @click="header.sortable ? handleSort(header.key) : null" :class="{ sortable: header.sortable }">
                            <div class="header-content">
                                <div v-if="header.sortable && sortColumn === header.key" class="sort-icons">
                                    <i v-if="sortDirection === 'asc'" class="i-andy-chevron-arrow-up i-andy-xs active"></i>
                                    <i v-else-if="sortDirection === 'desc'" class="i-andy-chevron-arrow-down i-andy-xs active"></i>
                                </div>
                                <span class="header-text">{{ header.text }}</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="rows && rows.length > 0" class="body">
                    <tr v-for="(row, rowIndex) in rows" @click="$emit('row-click', row)" :key="row.id || rowIndex" class="row" :class="getRowClass(row)">
                        <td v-for="header in headers" :key="header.key" class="cell" :class="getCellClass(header.key)">
                            <slot :name="`cell-${header.key}`" :value="row[header.key]" :row="row">
                                {{ row[header.key] }}
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <PaginationBar
            :compact="compactPagination"
            :pageNumber="pageNumber"
            :itemsPerPage="itemsPerPage"
            :totalItems="totalItems"
            :showPageNumbers="showPageNumbers"
            :showTotal="showTotal"
            :totalLabel="totalLabel"
            :maxVisiblePages="maxVisiblePages"
            @previous-page="$emit('back-button-click')"
            @next-page="$emit('forward-button-click')"
            @page-change="$emit('page-button-click', $event)"
        />
    </div>
</template>

<script>
import PaginationBar from '@/components/domain/ui/PaginationBar.vue'

export default {
    name: 'BaseTable',
    components: { PaginationBar },
    props: {
        compactPagination: {
            type: Boolean,
            default: false
        },
        headers: {
            type: Array,
            required: true
        },
        // expected structure example for each header:
        // [{ text: 'Created Date',
        // key: 'createdDate',
        // sortable: true,
        // visible: true,
        // blocked: true }]
        rows: {
            type: Array,
            required: true
        },
        // expected structure example for each row (id is optional):
        // [{ id: 1, createdDate: '2025-01-01', status: 'Pending', progressPercentage: '50%' }]
        // the key of the object will be the header.key and the value will be the value displayed in the cell
        pageNumber: {
            type: Number,
            required: false
        },
        itemsPerPage: {
            type: Number,
            required: false
        },
        totalItems: {
            type: [Number, String],
            required: false
        },
        showPageNumbers: {
            type: Boolean,
            default: false
        },
        showTotal: {
            type: Boolean,
            default: false
        },
        totalLabel: {
            type: String,
            default: ''
        },
        maxVisiblePages: {
            type: Number,
            default: 5
        },
        columnStyles: {
            type: Object,
            required: false,
            default: () => ({})
        },
        rowClass: {
            type: [String, Array, Object, Function],
            required: false,
            default: ''
        },
        sortColumn: {
            type: String,
            required: false,
            default: null
        },
        sortDirection: {
            type: String,
            required: false,
            default: null,
            validator: (value) => !value || ['asc', 'desc'].includes(value)
        }
    },
    computed: {
        hasPagination() {
            return this.pageNumber && this.itemsPerPage && this.totalItems && this.totalItems > this.itemsPerPage
        }
    },
    methods: {
        getCellClass(columnKey) {
            return this.columnStyles[columnKey] || ''
        },
        getRowClass(row) {
            return typeof this.rowClass === 'function' ? this.rowClass(row) : this.rowClass
        },
        handleSort(columnKey) {
            let newDirection = 'asc'
            if (this.sortColumn === columnKey) {
                newDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
            }
            this.$emit('sort-change', { column: columnKey, direction: newDirection })
        }
    }
}
</script>

<style lang="scss" scoped>
.base-table-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.base-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
    background-color: $color-white;

    .headers {
        width: 100%;
        height: 41px;
        background-color: #ede7db;
        position: sticky;
        top: 0;
        z-index: 10;

        tr {
            th {
                font-size: 14px;
                background-color: #ede7db;
                height: 100%;
                vertical-align: middle;
                padding: 8px;
                text-align: left;
                min-width: 100px;

                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                max-width: 200px;

                &.sortable {
                    cursor: pointer;
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;

                    .header-text {
                        flex: 1;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    .sort-icons {
                        margin-right: 8px;
                        flex-shrink: 0;

                        .i-andy-xs {
                            &.active {
                                color: #333;
                            }

                            &.inactive {
                                color: #ccc;
                            }
                        }
                    }
                }
            }

            th:hover {
                background-color: #e8e0d1;
            }
        }
    }

    .body {
        .row {
            cursor: pointer;
            .cell {
                font-size: 14px;
                height: 72px;
                padding: 8px;
                vertical-align: middle;
                min-width: 100px;

                &.bold-text {
                    font-family: $text-bold;
                }
            }
        }
    }
}

.table-wrapper {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    width: 100%;
    padding-bottom: 4px;
    background-color: #f4f2ed;

    &.has-pagination {
        height: calc(100% - 48px);
        max-height: calc(100% - 48px);
    }

    @media (max-width: 1023px) {
        overflow-x: auto;
    }
}

</style>
